import { Dispatch, SetStateAction } from 'react'

import useSWR from 'swr'

import { useCallMutation } from '@/common/api/client'
import { apiDefaultFetcher, apiDeleteFetcher, apiPutFetcher } from '@/common/api/fetcher'
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '@/common/api/pagination'
import {
  AiTagApiPath,
  CreateAiTagResponse,
  CreateAiTagVars,
  DeleteAiTagResponse,
  DeleteAiTagVars,
  DeleteAllAiTagsResponse,
  DeleteAllAiTagsVars,
  GetAiTagResponse,
  GetAiTagVars,
  GetAiTagsProgressResponse,
  GetTagEffectivenessResponse,
  GetTagEffectivenessVars,
  GetTagMappingsResponse,
  GetTagMappingsVars,
  ListAiTagsResponse,
  ListAiTagsVars,
  ListTagGenerationsResponse,
  ListTagGenerationsVars,
  PreviewTagProductsResponse,
  PreviewTagProductsVars,
  RestoreTagGenerationResponse,
  RestoreTagGenerationVars,
  StartAiAnalysisResponse,
  StartAiAnalysisVars,
  StartDiffAnalysisResponse,
  StartDiffAnalysisVars,
  UpdateAiTagResponse,
  UpdateAiTagVars
} from '@/common/api/params/aiTag'
import { SearchConditionBase, SearchConditionParam, useSearchCondition } from '@/common/api/search'

export const AiTagOrderBy = {
  IMPORTANCE_ASC: 'importance-asc',
  IMPORTANCE_DESC: 'importance-desc',
  OCCURRENCE_ASC: 'occurrence-asc',
  OCCURRENCE_DESC: 'occurrence-desc'
} as const

export type AiTagOrderBy = (typeof AiTagOrderBy)[keyof typeof AiTagOrderBy]

export interface AiTagSearchCondition extends SearchConditionBase {
  keyword: string | null
  category: string | null
  orderBy: AiTagOrderBy | null
}

const aiTagSearchConditionParams: SearchConditionParam[] = [
  { name: 'keyword', type: 'string' },
  { name: 'category', type: 'string' },
  { name: 'orderBy', type: 'string' },
  { name: 'sortOrder', type: 'string' }
]

export const AI_TAG_SEARCH_CONDITION_CLEAR: AiTagSearchCondition = {
  page: DEFAULT_PAGE,
  perPage: DEFAULT_PER_PAGE,
  keyword: null,
  category: null,
  orderBy: null
}

export const useAiTagSearchCondition = (): [
  AiTagSearchCondition,
  Dispatch<SetStateAction<AiTagSearchCondition>>,
  (page: number) => void
] => {
  return useSearchCondition<AiTagSearchCondition>(aiTagSearchConditionParams)
}

export const convertAiTagConditionToVars = (
  siteId: number,
  condition: AiTagSearchCondition
): ListAiTagsVars => ({
  site_id: siteId,
  page: condition.page,
  per_page: condition.perPage,
  sort_by: condition.orderBy?.split('-')[0],
  sort_order: condition.orderBy?.endsWith('asc')
    ? 'asc'
    : condition.orderBy?.endsWith('desc')
      ? 'desc'
      : undefined,
  ...(condition.keyword ? { search: condition.keyword } : {}),
  ...(condition.category ? { category: condition.category } : {})
})

// -----------------------------------------------
// GET hooks
// -----------------------------------------------

export const useAiTagListApi = (vars: ListAiTagsVars) => {
  return useSWR<ListAiTagsResponse, Response, [string, ListAiTagsVars]>(
    [AiTagApiPath.LIST_AI_TAGS, vars],
    apiDefaultFetcher
  )
}

export const useAiTagGetApi = (vars: GetAiTagVars) => {
  return useSWR<GetAiTagResponse, Response, [string, GetAiTagVars]>(
    [AiTagApiPath.GET_AI_TAG, vars],
    apiDefaultFetcher,
    { revalidateOnMount: true }
  )
}

export const useAiTagGenerationsApi = (vars: ListTagGenerationsVars) => {
  return useSWR<ListTagGenerationsResponse, Response, [string, ListTagGenerationsVars]>(
    [AiTagApiPath.LIST_TAG_GENERATIONS, vars],
    apiDefaultFetcher
  )
}

export const useAiTagEffectivenessApi = (vars: GetTagEffectivenessVars) => {
  return useSWR<GetTagEffectivenessResponse, Response, [string, GetTagEffectivenessVars]>(
    [AiTagApiPath.GET_TAG_EFFECTIVENESS, vars],
    apiDefaultFetcher
  )
}

export const useAiTagMappingsApi = (vars: GetTagMappingsVars | null) => {
  return useSWR<GetTagMappingsResponse, Response, [string, GetTagMappingsVars] | null>(
    vars ? [AiTagApiPath.GET_TAG_MAPPINGS, vars] : null,
    apiDefaultFetcher
  )
}

export const useAiTagPreviewApi = (vars: PreviewTagProductsVars | null) => {
  return useSWR<PreviewTagProductsResponse, Response, [string, PreviewTagProductsVars] | null>(
    vars ? [AiTagApiPath.PREVIEW_TAG_PRODUCTS, vars] : null,
    apiDefaultFetcher
  )
}

export const fetchAiTagProgress = (siteId: number): Promise<GetAiTagsProgressResponse> => {
  return apiDefaultFetcher<GetAiTagsProgressResponse>([
    AiTagApiPath.GET_AI_TAGS_PROGRESS,
    { site_id: siteId }
  ])
}

// -----------------------------------------------
// Mutation hooks
// -----------------------------------------------

export const useCreateAiTagMutation = (options?: {
  onSuccess?: (response: CreateAiTagResponse) => void
  onError?: (error: Response) => void
}) => {
  return useCallMutation<CreateAiTagResponse, CreateAiTagVars>(AiTagApiPath.CREATE_AI_TAG, options)
}

export const useStartAiAnalysisMutation = (options?: {
  onSuccess?: (response: StartAiAnalysisResponse) => void
  onError?: (error: Response) => void
}) => {
  return useCallMutation<StartAiAnalysisResponse, StartAiAnalysisVars>(
    AiTagApiPath.START_AI_ANALYSIS,
    options
  )
}

export const useStartDiffAnalysisMutation = (options?: {
  onSuccess?: (response: StartDiffAnalysisResponse) => void
  onError?: (error: Response) => void
}) => {
  return useCallMutation<StartDiffAnalysisResponse, StartDiffAnalysisVars>(
    AiTagApiPath.START_DIFF_ANALYSIS,
    options
  )
}

export const useDeleteAllAiTagsMutation = (options?: {
  onSuccess?: (response: DeleteAllAiTagsResponse) => void
  onError?: (error: Response) => void
}) => {
  return useCallMutation<DeleteAllAiTagsResponse, DeleteAllAiTagsVars>(
    AiTagApiPath.DELETE_ALL_AI_TAGS,
    options,
    apiDeleteFetcher
  )
}

export const useDeleteAiTagMutation = (options?: {
  onSuccess?: (response: DeleteAiTagResponse) => void
  onError?: (error: Response) => void
}) => {
  return useCallMutation<DeleteAiTagResponse, DeleteAiTagVars>(
    AiTagApiPath.DELETE_AI_TAG,
    options,
    apiDeleteFetcher
  )
}

export const useUpdateAiTagMutation = (options?: {
  onSuccess?: (response: UpdateAiTagResponse) => void
  onError?: (error: Response) => void
}) => {
  return useCallMutation<UpdateAiTagResponse, UpdateAiTagVars>(
    AiTagApiPath.UPDATE_AI_TAG,
    options,
    apiPutFetcher
  )
}

export const useRestoreTagGenerationMutation = (options?: {
  onSuccess?: (response: RestoreTagGenerationResponse) => void
  onError?: (error: Response) => void
}) => {
  return useCallMutation<RestoreTagGenerationResponse, RestoreTagGenerationVars>(
    AiTagApiPath.RESTORE_TAG_GENERATION,
    options
  )
}
